<template>
  <div class="settings-card history-card">
    <div class="card-header justify-between">
      <div class="flex align-items-center">
        <feather-icon class="card-icon" icon="ClockIcon"/>
        <span class="card-title">Histórico de Ativação</span>
      </div>
      <div class="inline-flex gap-2">
        <vx-tooltip class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Baixar histórico completo (xlsx)">
          <feather-icon :class="{ 'icon-disabled': exporting }" @click="downloadHistory" icon="DownloadIcon"/>
        </vx-tooltip>
        <vx-tooltip class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Atualizar histórico">
          <feather-icon @click="loadHistory(true)" icon="RefreshCwIcon"/>
        </vx-tooltip>
      </div>
    </div>

    <div class="card-divider"></div>

    <div class="card-content">
      <div v-if="loading && items.length === 0" class="disabled-content">
        <p class="disabled-message">Carregando histórico...</p>
      </div>

      <div v-else-if="items.length === 0" class="disabled-content">
        <p class="disabled-message">Nenhum registro de desativação ou reativação encontrado para este tenant.</p>
      </div>

      <div v-else class="history-timeline">
        <div
          v-for="item in items"
          :key="item.id"
          class="timeline-entry"
        >
          <div class="timeline-badge" :class="item.action_type === 'tenant_deactivation' ? 'badge-deactivation' : 'badge-restore'">
            <feather-icon :icon="item.action_type === 'tenant_deactivation' ? 'XCircleIcon' : 'CheckCircleIcon'" size="14"/>
          </div>

          <div class="timeline-body">
            <div class="timeline-header">
              <span class="action-label" :class="item.action_type === 'tenant_deactivation' ? 'label-deactivation' : 'label-restore'">
                {{ item.action_type === 'tenant_deactivation' ? 'Desativado' : 'Reativado' }}
              </span>
              <span class="origin-badge" :class="item.scheduled_action ? 'origin-scheduled' : 'origin-direct'">
                {{ item.scheduled_action ? 'Agendada' : 'Manual' }}
              </span>
            </div>

            <div class="timeline-details">
              <div class="detail-item">
                <span class="detail-label">Quando</span>
                <span class="detail-value">{{ formatDate(item.occurred_at) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Por</span>
                <span class="detail-value">{{ resolveAuthor(item) }}</span>
              </div>
              <div class="detail-item" v-if="item.payload && item.payload.motivo">
                <span class="detail-label">Motivo</span>
                <span class="detail-value reason-content">{{ item.payload.motivo }}</span>
              </div>
              <template v-if="item.source === 'approval' && item.scheduled_action">
                <div class="detail-item" v-if="item.scheduled_action.executes_at">
                  <span class="detail-label">Agendado para</span>
                  <span class="detail-value">{{ formatDate(item.scheduled_action.executes_at) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Solicitação</span>
                  <span class="detail-value">
                    <a class="schedule-link cursor-pointer" @click="goToRequest(item.id)">Ver solicitação #{{ item.id }}</a>
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasMore" class="load-more-wrapper">
        <vs-button type="border" size="small" :disabled="loading" @click="loadMore">
          {{ loading ? 'Carregando...' : 'Ver mais' }}
        </vs-button>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'TenantHistoryCard',
  props: {
    tenantData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      items: [],
      page: 1,
      totalPages: 1,
      loading: false,
      exporting: false
    }
  },
  computed: {
    tenantId() {
      return this.tenantData && this.tenantData.id ? this.tenantData.id : null
    },
    hasMore() {
      return this.page < this.totalPages
    },
    tenantAlias() {
      if (this.tenantData && this.tenantData.fqdn) return this.tenantData.fqdn.split('.')[0]

      return this.tenantId ? `tenant-${this.tenantId}` : 'tenant'
    }
  },
  watch: {
    tenantData: {
      immediate: true,
      handler(val) {
        if (val && val.id) {
          this.loadHistory(true)
        }
      }
    }
  },
  methods: {
    async loadHistory(reset = false) {
      if (!this.tenantId) return

      if (reset) {
        this.items = []
        this.page = 1
        this.totalPages = 1
      }

      this.loading = true
      try {
        const data = await this.$store.dispatch('approvals/fetchTenantHistory', {
          tenantId: this.tenantId,
          page: this.page,
          limit: 2
        })

        if (data) {
          this.totalPages = data.total_pages || 1
          this.items = reset ? (data.items || []) : [...this.items, ...(data.items || [])]
        }
      } catch (error) {
        console.error(error)
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Não foi possível carregar o histórico de ativação.'
        })
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (!this.hasMore || this.loading) return
      const nextPage = this.page + 1
      this.loading = true
      try {
        const data = await this.$store.dispatch('approvals/fetchTenantHistory', {
          tenantId: this.tenantId,
          page: nextPage,
          limit: 2
        })
        if (data) {
          this.page = nextPage
          this.totalPages = data.total_pages || 1
          this.items = [...this.items, ...(data.items || [])]
        }
      } catch (error) {
        console.error(error)
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Não foi possível carregar o histórico de ativação.'
        })
      } finally {
        this.loading = false
      }
    },
    async downloadHistory() {
      if (!this.tenantId || this.exporting) return

      this.exporting = true
      try {
        const data = await this.$store.dispatch('approvals/exportTenantHistory', { tenantId: this.tenantId })
        if (!data) throw new Error('Resposta vazia')

        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `historico-ativacao-${this.tenantAlias}.xlsx`
        link.click()
        window.URL.revokeObjectURL(link.href)
      } catch (error) {
        console.error(error)
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Não foi possível baixar o histórico de ativação.'
        })
      } finally {
        this.exporting = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return 'Não informado'
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return 'Data inválida'
      return d.toLocaleString('pt-BR')
    },
    resolveAuthor(item) {
      if (item.scheduled_action && item.scheduled_action.executor) {
        return item.scheduled_action.executor.name
      }
      if (item.requester && item.requester.name) {
        return item.requester.name
      }
      return 'Não informado'
    },
    goToRequest(id) {
      this.$router.push(`/approvals/${id}`)
    }
  }
}
</script>

<style scoped lang="scss">

.card-header {
  display: flex;
  padding: 1rem;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}

.align-items-center {
  align-items: center !important;
}

.card-icon {
  width: 16px;
  height: 16px;
  color: #666;
  margin-right: 0.5rem;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.card-divider {
  height: 1px;
  background-color: #e8e8e8;
}

.card-content {
  flex: 1;
  padding: 1rem;
}

.disabled-content {
  text-align: center;
  padding: 2rem 1rem;
}

.disabled-message {
  color: #666;
  font-size: 13px;
  margin: 0;
  font-style: italic;
}

.icon-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.history-timeline {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.timeline-entry {
  position: relative;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.timeline-entry:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 13px;
  top: 38px;
  bottom: -1rem;
  width: 2px;
  background-color: #e5e8eb;
}

.timeline-badge {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin-top: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.badge-deactivation {
  background-color: rgba(var(--vs-danger), 0.12);
  color: rgba(var(--vs-danger), 1);
}

.badge-restore {
  background-color: rgba(var(--vs-success), 0.12);
  color: rgba(var(--vs-success), 1);
}

.timeline-body {
  flex: 1;
  min-width: 0;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 0.75rem;
}

.timeline-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.action-label {
  font-weight: 600;
  font-size: 13px;
}

.label-deactivation {
  color: rgba(var(--vs-danger), 1);
}

.label-restore {
  color: rgba(var(--vs-success), 1);
}

.origin-badge {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.origin-direct {
  background-color: rgba(var(--vs-primary), 0.12);
  color: rgba(var(--vs-primary), 1);
}

.origin-scheduled {
  background-color: rgba(var(--vs-warning), 0.16);
  color: rgba(var(--vs-warning), 1);
}

.timeline-details {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.detail-item {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  gap: 0.5rem;
  align-items: start;
  font-size: 12px;
}

.detail-label {
  color: #666;
  font-weight: 500;
}

.detail-value {
  min-width: 0;
  color: #333;
}

@media (max-width: 576px) {
  .detail-item {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.15rem;
  }
}

.load-more-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.schedule-link {
  color: rgba(var(--vs-primary), 1);
}

.reason-content {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 0.25rem;
}
</style>
